import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';

export enum AuditAction {
  // Organization actions
  ORG_CREATED = 'org.created',
  ORG_DELETED = 'org.deleted',
  ORG_SETTINGS_CHANGED = 'org.settings.changed',
  
  // Member actions
  MEMBER_ADDED = 'member.added',
  MEMBER_REMOVED = 'member.removed',
  MEMBER_ROLE_CHANGED = 'member.role.changed',
  
  // Team actions
  TEAM_CREATED = 'team.created',
  TEAM_DELETED = 'team.deleted',
  TEAM_MEMBER_ADDED = 'team.member.added',
  TEAM_MEMBER_REMOVED = 'team.member.removed',
  TEAM_MEMBER_ROLE_CHANGED = 'team.member.role.changed',
  
  // Project actions
  PROJECT_CREATED = 'project.created',
  PROJECT_DELETED = 'project.deleted',
  PROJECT_OWNER_CHANGED = 'project.owner.changed',
  PROJECT_CONTRIBUTOR_ADDED = 'project.contributor.added',
  PROJECT_CONTRIBUTOR_REMOVED = 'project.contributor.removed',
  PROJECT_STATE_CHANGED = 'project.state.changed',

  // Project state management
  PROJECT_STATE_CREATED = 'project_state.created',
  PROJECT_STATE_UPDATED = 'project_state.updated',
  PROJECT_STATE_DEACTIVATED = 'project_state.deactivated',
  PROJECT_STATES_REORDERED = 'project_states.reordered',
  PROJECT_STATES_SEEDED = 'project_states.seeded',

  // Project label management
  PROJECT_LABEL_CREATED = 'project_label.created',
  PROJECT_LABEL_UPDATED = 'project_label.updated',
  PROJECT_LABEL_DEACTIVATED = 'project_label.deactivated',
  PROJECT_LABELS_MERGED = 'project_labels.merged',

  // Migration actions
  PROJECT_MIGRATION_COMPLETED = 'project_migration.completed',
  PROJECT_MIGRATION_ROLLBACK = 'project_migration.rollback',

  // Update/Feed actions
  UPDATE_CREATED = 'update.created',
  UPDATE_DELETED = 'update.deleted',
  COMMENT_CREATED = 'comment.created',
  COMMENT_DELETED = 'comment.deleted',
  POLL_CREATED = 'poll.created',
  POLL_VOTED = 'poll.voted',

  // Engagement actions
  REACTION_ADDED = 'reaction.added',
  REACTION_REMOVED = 'reaction.removed',

  // Billing actions
  BILLING_PLAN_CHANGED = 'billing.plan.changed',
  BILLING_METHOD_UPDATED = 'billing.method.updated',
  
  // Security actions
  INVITATION_SENT = 'invitation.sent',
  INVITATION_ACCEPTED = 'invitation.accepted',
  INVITATION_REVOKED = 'invitation.revoked',
  SUPER_ADMIN_ASSIGNED = 'superadmin.assigned',
  SUPER_ADMIN_REVOKED = 'superadmin.revoked',

  // Debug actions
  DEBUG_ACCESS = 'debug.access',
  DEBUG_ERROR = 'debug.error',
  DEBUG_MODE_ENABLED = 'debug.mode.enabled',
  DEBUG_MODE_DISABLED = 'debug.mode.disabled',
}

export enum AuditSeverity {
  INFO = 'info',
  WARNING = 'warning',
  CRITICAL = 'critical',
}

export interface AuditLogEntry {
  id?: string;
  action: AuditAction;
  severity: AuditSeverity;
  orgId: string;
  actorId: string;
  actorEmail?: string;
  actorName?: string;
  targetId?: string;
  targetType?: 'user' | 'team' | 'project' | 'org';
  targetEmail?: string;
  metadata?: Record<string, any>;
  description: string;
  ipAddress?: string;
  userAgent?: string;
  timestamp: Timestamp | ReturnType<typeof serverTimestamp>;
}

export interface AuditContext {
  orgId: string;
  actorId: string;
  actorEmail?: string;
  actorName?: string;
  ipAddress?: string;
  userAgent?: string;
}

const CRITICAL_ACTIONS = [
  AuditAction.ORG_DELETED,
  AuditAction.MEMBER_ROLE_CHANGED,
  AuditAction.BILLING_PLAN_CHANGED,
  AuditAction.BILLING_METHOD_UPDATED,
  AuditAction.SUPER_ADMIN_ASSIGNED,
  AuditAction.SUPER_ADMIN_REVOKED,
  AuditAction.PROJECT_OWNER_CHANGED,
];

const WARNING_ACTIONS = [
  AuditAction.MEMBER_REMOVED,
  AuditAction.TEAM_DELETED,
  AuditAction.PROJECT_DELETED,
  AuditAction.INVITATION_REVOKED,
];

function getSeverity(action: AuditAction): AuditSeverity {
  if (CRITICAL_ACTIONS.includes(action)) {
    return AuditSeverity.CRITICAL;
  }
  if (WARNING_ACTIONS.includes(action)) {
    return AuditSeverity.WARNING;
  }
  return AuditSeverity.INFO;
}

export async function logAuditEvent(
  action: AuditAction,
  context: AuditContext,
  details: {
    targetId?: string;
    targetType?: 'user' | 'team' | 'project' | 'org';
    targetEmail?: string;
    metadata?: Record<string, any>;
    description: string;
  }
): Promise<void> {
  try {
    const auditRef = doc(collection(db, 'orgs', context.orgId, 'auditLogs'));
    
    const entry: AuditLogEntry = {
      action,
      severity: getSeverity(action),
      orgId: context.orgId,
      actorId: context.actorId,
      actorEmail: context.actorEmail || '',
      actorName: context.actorName || '',
      description: details.description,
      timestamp: serverTimestamp(),
    };

    // Only add optional fields if they have values
    if (details.targetId) entry.targetId = details.targetId;
    if (details.targetType) entry.targetType = details.targetType;
    if (details.targetEmail) entry.targetEmail = details.targetEmail;
    if (details.metadata) entry.metadata = details.metadata;
    if (context.ipAddress) entry.ipAddress = context.ipAddress;
    if (context.userAgent) entry.userAgent = context.userAgent;

    await setDoc(auditRef, entry);
  } catch (error) {
    // Log to console but don't fail the main operation
    console.error('Failed to log audit event:', error);
  }
}

export async function getAuditLogs(
  orgId: string,
  filters?: {
    action?: AuditAction;
    actorId?: string;
    targetId?: string;
    severity?: AuditSeverity;
    startDate?: Date;
    endDate?: Date;
    limit?: number;
  }
): Promise<AuditLogEntry[]> {
  let q = query(
    collection(db, 'orgs', orgId, 'auditLogs'),
    orderBy('timestamp', 'desc')
  );
  
  if (filters?.action) {
    q = query(q, where('action', '==', filters.action));
  }
  
  if (filters?.actorId) {
    q = query(q, where('actorId', '==', filters.actorId));
  }
  
  if (filters?.targetId) {
    q = query(q, where('targetId', '==', filters.targetId));
  }
  
  if (filters?.severity) {
    q = query(q, where('severity', '==', filters.severity));
  }
  
  if (filters?.startDate) {
    q = query(q, where('timestamp', '>=', Timestamp.fromDate(filters.startDate)));
  }
  
  if (filters?.endDate) {
    q = query(q, where('timestamp', '<=', Timestamp.fromDate(filters.endDate)));
  }
  
  if (filters?.limit) {
    q = query(q, firestoreLimit(filters.limit));
  }
  
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as AuditLogEntry[];
}

// Helper functions for common audit scenarios

export async function auditMemberRoleChange(
  context: AuditContext,
  targetUserId: string,
  targetEmail: string,
  oldRole: string,
  newRole: string
): Promise<void> {
  await logAuditEvent(
    AuditAction.MEMBER_ROLE_CHANGED,
    context,
    {
      targetId: targetUserId,
      targetType: 'user',
      targetEmail,
      metadata: {
        oldRole,
        newRole,
      },
      description: `Changed ${targetEmail} role from ${oldRole} to ${newRole}`,
    }
  );
}

export async function auditProjectDeletion(
  context: AuditContext,
  projectId: string,
  projectName: string
): Promise<void> {
  await logAuditEvent(
    AuditAction.PROJECT_DELETED,
    context,
    {
      targetId: projectId,
      targetType: 'project',
      metadata: {
        projectName,
        deletionType: 'soft', // We're using soft deletes
      },
      description: `Deleted project "${projectName}"`,
    }
  );
}

export async function auditTeamCreation(
  context: AuditContext,
  teamId: string,
  teamName: string
): Promise<void> {
  await logAuditEvent(
    AuditAction.TEAM_CREATED,
    context,
    {
      targetId: teamId,
      targetType: 'team',
      metadata: {
        teamName,
      },
      description: `Created team "${teamName}"`,
    }
  );
}

export async function auditInvitationSent(
  context: AuditContext,
  invitationId: string,
  inviteeEmail: string,
  role?: string
): Promise<void> {
  await logAuditEvent(
    AuditAction.INVITATION_SENT,
    context,
    {
      targetId: invitationId,
      targetType: 'user',
      targetEmail: inviteeEmail,
      metadata: {
        role,
      },
      description: `Sent invitation to ${inviteeEmail}${role ? ` with ${role} role` : ''}`,
    }
  );
}

export async function auditBillingChange(
  context: AuditContext,
  changeType: 'plan' | 'method',
  oldValue: string,
  newValue: string,
  metadata?: Record<string, any>
): Promise<void> {
  const action = changeType === 'plan' 
    ? AuditAction.BILLING_PLAN_CHANGED 
    : AuditAction.BILLING_METHOD_UPDATED;
  
  await logAuditEvent(
    action,
    context,
    {
      targetType: 'org',
      metadata: {
        oldValue,
        newValue,
        ...metadata,
      },
      description: `Changed billing ${changeType} from ${oldValue} to ${newValue}`,
    }
  );
}

export async function auditOwnershipTransfer(
  context: AuditContext,
  resourceType: 'project' | 'org',
  resourceId: string,
  resourceName: string,
  newOwnerId: string,
  newOwnerEmail: string
): Promise<void> {
  const action = resourceType === 'project'
    ? AuditAction.PROJECT_OWNER_CHANGED
    : AuditAction.MEMBER_ROLE_CHANGED;

  await logAuditEvent(
    action,
    context,
    {
      targetId: resourceId,
      targetType: resourceType,
      metadata: {
        resourceName,
        newOwnerId,
        newOwnerEmail,
        previousOwnerId: context.actorId,
      },
      description: `Transferred ownership of ${resourceType} "${resourceName}" to ${newOwnerEmail}`,
    }
  );
}

export async function auditUpdateCreated(
  context: AuditContext,
  updateId: string,
  updateType: 'post' | 'poll',
  metadata?: {
    projectId?: string;
    projectName?: string;
    teamId?: string;
    teamName?: string;
    hasImages?: boolean;
  }
): Promise<void> {
  const projectInfo = metadata?.projectName ? ` in "${metadata.projectName}"` : '';
  const description = updateType === 'poll'
    ? `Created a poll${projectInfo}`
    : `Posted an update${projectInfo}`;

  await logAuditEvent(
    updateType === 'poll' ? AuditAction.POLL_CREATED : AuditAction.UPDATE_CREATED,
    context,
    {
      targetId: updateId,
      metadata,
      description,
    }
  );
}

export async function auditCommentCreated(
  context: AuditContext,
  commentId: string,
  updateId: string,
  metadata?: {
    isReply?: boolean;
    parentCommentId?: string;
  }
): Promise<void> {
  const description = metadata?.isReply
    ? 'Replied to a comment'
    : 'Commented on an update';

  await logAuditEvent(
    AuditAction.COMMENT_CREATED,
    context,
    {
      targetId: commentId,
      metadata: {
        ...metadata,
        updateId,
      },
      description,
    }
  );
}

export async function auditReactionAdded(
  context: AuditContext,
  targetId: string,
  targetType: 'update' | 'comment',
  emoji: string
): Promise<void> {
  await logAuditEvent(
    AuditAction.REACTION_ADDED,
    context,
    {
      targetId,
      metadata: {
        targetType,
        emoji,
      },
      description: `Reacted with ${emoji}`,
    }
  );
}

export async function auditPollVoted(
  context: AuditContext,
  pollId: string,
  optionIds: string[],
  metadata?: {
    projectName?: string;
  }
): Promise<void> {
  const projectInfo = metadata?.projectName ? ` in "${metadata.projectName}"` : '';

  await logAuditEvent(
    AuditAction.POLL_VOTED,
    context,
    {
      targetId: pollId,
      metadata: {
        ...metadata,
        optionIds,
        optionCount: optionIds.length,
      },
      description: `Voted on a poll${projectInfo}`,
    }
  );
}